import React, { useEffect, useRef, useState } from "react";
import { formatCurrency } from "../../../utils/formatCurrency";
import {
  AcquisitionCost,
  CATEGORY_OPTIONS,
  SEGMENT_LABELS,
} from "../../../types/unit-economics.types";
import {
  useAcquisitionCostsQuery,
  useDeleteAcquisitionCostMutation,
} from "../../../hooks/useUnitEconomics";
import AcquisitionCostModal from "./AcquisitionCostModal";

const categoryLabel = (category: string): string =>
  CATEGORY_OPTIONS.find((c) => c.value === category)?.label ?? category;

/** Returns the Polish channel label; legacy `null`/absent rows show "Nieprzypisane". */
const channelLabel = (cost: AcquisitionCost): React.ReactElement => {
  if (!cost.channel) {
    return (
      <span className="text-neutral-400">{SEGMENT_LABELS.unassigned}</span>
    );
  }
  return <>{SEGMENT_LABELS[cost.channel]}</>;
};

const isApiCost = (cost: AcquisitionCost): boolean => cost.source === "api";

/** Only manual (or legacy, source-less) entries can be edited in the panel. */
const isEditableCost = (cost: AcquisitionCost): boolean =>
  cost.source === undefined || cost.source === "manual";

/**
 * Text label of the source badge: "Ręcznie", "API · name", plain "API";
 * an unknown (future) source is shown as its raw value.
 */
const sourceLabel = (cost: AcquisitionCost): string => {
  if (isApiCost(cost)) {
    return cost.createdByKeyName ? `API · ${cost.createdByKeyName}` : "API";
  }
  return isEditableCost(cost) ? "Ręcznie" : String(cost.source);
};

/** Question shown in the inline delete confirmation. */
const deleteConfirmationText = (cost: AcquisitionCost): string => {
  if (!isApiCost(cost)) return "Na pewno usunąć?";
  const details = [`klucz: ${cost.createdByKeyName ?? "nieznany"}`];
  if (cost.externalRef) details.push(`ref: ${cost.externalRef}`);
  return `Usunąć wpis z API (${details.join(", ")})? Ponowny zapis utworzy go na nowo.`;
};

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("pl-PL");

interface AcquisitionCostsSectionProps {
  /** Whether the current user can perform write actions (admin only). */
  canWrite: boolean;
}

const AcquisitionCostsSection: React.FC<AcquisitionCostsSectionProps> = ({
  canWrite,
}) => {
  const { data: costs, isLoading, error } = useAcquisitionCostsQuery();
  const deleteMutation = useDeleteAcquisitionCostMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCost, setEditingCost] = useState<AcquisitionCost | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // The "Usuń" button disappears when confirming — move focus to "Anuluj".
  const cancelDeleteRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (confirmDeleteId) cancelDeleteRef.current?.focus();
  }, [confirmDeleteId]);

  // Trigger element that opened the modal — focus returns here on close.
  const modalTriggerRef = useRef<HTMLElement | null>(null);

  const openCreate = (e: React.MouseEvent<HTMLButtonElement>) => {
    modalTriggerRef.current = e.currentTarget;
    setEditingCost(null);
    setModalOpen(true);
  };

  const openEdit = (
    e: React.MouseEvent<HTMLButtonElement>,
    cost: AcquisitionCost,
  ) => {
    modalTriggerRef.current = e.currentTarget;
    setEditingCost(cost);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    // Restore focus to the button that opened the modal.
    modalTriggerRef.current?.focus();
  };

  const confirmDelete = (cost: AcquisitionCost) => {
    deleteMutation.mutate(cost.id, {
      onSuccess: () => setConfirmDeleteId(null),
    });
  };

  return (
    <div className="bg-white shadow-sm rounded-lg border border-neutral-200">
      <div className="px-6 py-4 border-b border-neutral-200 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold text-neutral-900">
            Koszty pozyskania klientów
          </h3>
          <p className="text-sm text-neutral-600">
            Zmiana kosztów (ręczna lub z API) pojawi się w CAC po ok. 5 min.
          </p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={openCreate}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            aria-haspopup="dialog"
          >
            Dodaj koszt
          </button>
        )}
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <div
            role="status"
            aria-label="Ładowanie"
            className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"
          />
        </div>
      )}

      {error && (
        <div className="m-6 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-600">Błąd ładowania kosztów</p>
        </div>
      )}

      {!isLoading && !error && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[40rem]">
            <thead className="bg-neutral-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase"
                >
                  Data
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase"
                >
                  Kategoria
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase"
                >
                  Kanał
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase"
                >
                  Kwota
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase"
                >
                  Opis
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase"
                >
                  Źródło
                </th>
                {canWrite && (
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase"
                  >
                    Akcje
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {(!costs || costs.length === 0) && (
                <tr>
                  <td
                    colSpan={canWrite ? 7 : 6}
                    className="px-6 py-8 text-center text-neutral-500"
                  >
                    Brak kosztów pozyskania
                  </td>
                </tr>
              )}
              {costs?.map((cost) => {
                const label = sourceLabel(cost);
                return (
                  <tr key={cost.id} className="hover:bg-neutral-50">
                    <td className="px-6 py-4 text-sm text-neutral-600">
                      {formatDate(cost.incurredAt)}
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-700">
                      {categoryLabel(cost.category)}
                    </td>
                    <td className="px-6 py-4 text-sm">{channelLabel(cost)}</td>
                    <td className="px-6 py-4 text-sm font-mono">
                      {formatCurrency(cost.amountCents / 100)}
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-600">
                      {cost.description || "-"}
                      {cost.externalRef && (
                        <div className="font-mono text-xs text-neutral-500 break-all">
                          {cost.externalRef}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span
                        title={label}
                        className={`inline-block max-w-[16rem] truncate align-middle rounded px-2 py-0.5 text-xs font-medium ${
                          isApiCost(cost)
                            ? "bg-blue-100 text-blue-900"
                            : "bg-neutral-100 text-neutral-800"
                        }`}
                      >
                        {label}
                      </span>
                    </td>
                    {canWrite && (
                      <td className="px-6 py-4 text-sm">
                        {confirmDeleteId === cost.id ? (
                          <div
                            role="alertdialog"
                            aria-live="polite"
                            aria-label="Potwierdzenie usunięcia kosztu"
                            className="flex flex-wrap items-center gap-x-3 gap-y-1"
                          >
                            <span className="text-neutral-700 break-words min-w-0">
                              {deleteConfirmationText(cost)}
                            </span>
                            <button
                              type="button"
                              onClick={() => confirmDelete(cost)}
                              disabled={deleteMutation.isPending}
                              className="text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                            >
                              {deleteMutation.isPending
                                ? "Usuwanie..."
                                : "Tak, usuń"}
                            </button>
                            <button
                              ref={cancelDeleteRef}
                              type="button"
                              onClick={() => setConfirmDeleteId(null)}
                              disabled={deleteMutation.isPending}
                              className="text-neutral-600 hover:text-neutral-800 font-medium disabled:opacity-50"
                            >
                              Anuluj
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            {isEditableCost(cost) && (
                              <button
                                type="button"
                                onClick={(e) => openEdit(e, cost)}
                                aria-haspopup="dialog"
                                className="text-primary-600 hover:text-primary-800 font-medium"
                              >
                                Edytuj
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => setConfirmDeleteId(cost.id)}
                              aria-label={`Usuń koszt: ${
                                cost.description || categoryLabel(cost.category)
                              }`}
                              className="text-red-600 hover:text-red-800 font-medium"
                            >
                              Usuń
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <AcquisitionCostModal cost={editingCost} onClose={closeModal} />
      )}
    </div>
  );
};

export default AcquisitionCostsSection;
